import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLang, withLang } from "../i18n";
import { colors, fontHeading } from "../theme";
import { SITE } from "../siteConfig";

export default function Nav() {
  const { lang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = withLang("/", lang);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Real <a href> links (so search engines can follow them); the click is
  // still handled in-app so the page doesn't reload.
  const goTo = (hash, e) => {
    e?.preventDefault();
    setMenuOpen(false);
    if (location.pathname.replace(/\/?$/, "/") !== homePath) {
      navigate(`${homePath}${hash ? "#" + hash : ""}`);
    } else if (hash) {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const sectionHref = (hash) => `${homePath}${hash ? "#" + hash : ""}`;

  const switchLang = (targetLang, e) => {
    e?.preventDefault();
    setMenuOpen(false);
    navigate(withLang(location.pathname, targetLang) + location.hash);
  };

  return (
    <>
      <div style={s.topBar}>
        <div style={s.topBarInner}>
          <span style={s.topBarItem}>{SITE.address}</span>
          <span style={s.topBarSep} className="hide-mobile">
            ·
          </span>
          <a href={`tel:${SITE.phone.replace(/\s+/g, "")}`} style={s.topBarLink} className="link-hover">
            {SITE.phone}
          </a>
          <span style={{ flex: 1 }} />
          <span style={s.topBarItem} className="hide-mobile">
            {t.topBarHours}
          </span>
        </div>
      </div>

      <nav style={{ ...s.nav, boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.06)" : "none" }}>
        <div style={s.navInner}>
          <a href={homePath} style={s.logo} onClick={(e) => goTo("", e)}>
            <img src="/images/logo.png" alt={SITE.name} width={332} height={104} style={s.logoImg} />
          </a>
          <div style={s.navLinks} className="nav-links">
            <a href={sectionHref("")} style={s.navLink} className="link-hover" onClick={(e) => goTo("", e)}>
              {t.navHome}
            </a>
            <a href={sectionHref("coches")} style={s.navLink} className="link-hover" onClick={(e) => goTo("coches", e)}>
              {t.navCars}
            </a>
            <a href={sectionHref("nosotros")} style={s.navLink} className="link-hover" onClick={(e) => goTo("nosotros", e)}>
              {t.navAbout}
            </a>
            <a href={sectionHref("contacto")} style={s.navLink} className="link-hover" onClick={(e) => goTo("contacto", e)}>
              {t.navContact}
            </a>
            <div style={s.langToggle}>
              <a
                href={withLang(location.pathname, "es")}
                hrefLang="es"
                style={{ ...s.langBtn, ...(lang === "es" ? s.langBtnActive : {}) }}
                onClick={(e) => switchLang("es", e)}
              >
                ES
              </a>
              <span style={s.langSep}>/</span>
              <a
                href={withLang(location.pathname, "en")}
                hrefLang="en"
                style={{ ...s.langBtn, ...(lang === "en" ? s.langBtnActive : {}) }}
                onClick={(e) => switchLang("en", e)}
              >
                EN
              </a>
            </div>
          </div>
          <button style={s.hamburger} className="hamburger" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu">
            <span style={{ ...s.bar, transform: menuOpen ? "rotate(45deg) translate(5px,5px)" : "none" }} />
            <span style={{ ...s.bar, opacity: menuOpen ? 0 : 1 }} />
            <span style={{ ...s.bar, transform: menuOpen ? "rotate(-45deg) translate(5px,-5px)" : "none" }} />
          </button>
        </div>
        {menuOpen && (
          <div style={s.mobileMenu} className="fade">
            <a href={sectionHref("")} style={s.mobileLink} onClick={(e) => goTo("", e)}>
              {t.navHome}
            </a>
            <a href={sectionHref("coches")} style={s.mobileLink} onClick={(e) => goTo("coches", e)}>
              {t.navCars}
            </a>
            <a href={sectionHref("nosotros")} style={s.mobileLink} onClick={(e) => goTo("nosotros", e)}>
              {t.navAbout}
            </a>
            <a href={sectionHref("contacto")} style={s.mobileLink} onClick={(e) => goTo("contacto", e)}>
              {t.navContact}
            </a>
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <a
                href={withLang(location.pathname, "es")}
                hrefLang="es"
                style={{ ...s.langBtn, ...(lang === "es" ? s.langBtnActive : {}) }}
                onClick={(e) => switchLang("es", e)}
              >
                ES
              </a>
              <a
                href={withLang(location.pathname, "en")}
                hrefLang="en"
                style={{ ...s.langBtn, ...(lang === "en" ? s.langBtnActive : {}) }}
                onClick={(e) => switchLang("en", e)}
              >
                EN
              </a>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

const s = {
  topBar: { background: colors.ink, color: "#eee" },
  topBarInner: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "8px 40px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontSize: 12,
    letterSpacing: "0.02em",
  },
  topBarItem: { color: "#cfcfcf" },
  topBarLink: { color: "#fff", textDecoration: "none", fontWeight: 500 },
  topBarSep: { color: "#666" },
  nav: {
    position: "sticky",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    background: "#ffffff",
    transition: "box-shadow 0.3s ease",
    borderBottom: `1px solid ${colors.border}`,
  },
  navInner: {
    maxWidth: 1280,
    margin: "0 auto",
    padding: "14px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: { display: "flex", alignItems: "center", cursor: "pointer", textDecoration: "none" },
  logoImg: { height: 44, width: "auto", display: "block" },
  navLinks: { display: "flex", alignItems: "center", gap: 30 },
  navLink: {
    background: "none",
    textDecoration: "none",
    border: "none",
    color: colors.ink,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "0.06em",
    cursor: "pointer",
    fontFamily: fontHeading,
    transition: "color 0.2s",
  },
  langToggle: { display: "flex", alignItems: "center", gap: 4, marginLeft: 6 },
  langBtn: {
    background: "none",
    textDecoration: "none",
    border: "none",
    color: colors.muted,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.05em",
    cursor: "pointer",
    padding: "4px 6px",
    fontFamily: fontHeading,
  },
  langBtnActive: { color: colors.red },
  langSep: { color: colors.faint, fontSize: 12 },
  hamburger: {
    display: "none",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    flexDirection: "column",
    gap: 5,
  },
  bar: { display: "block", width: 24, height: 2, background: colors.ink, transition: "all 0.3s", borderRadius: 2 },
  mobileMenu: {
    padding: "10px 24px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    borderTop: `1px solid ${colors.border}`,
  },
  mobileLink: {
    background: "none",
    textDecoration: "none",
    border: "none",
    color: colors.ink,
    fontSize: 15,
    fontWeight: 600,
    letterSpacing: "0.04em",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: fontHeading,
    padding: "4px 0",
  },
};
